import {
  Box,
  Button,
  Flex,
  Icon,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { IoMdArrowRoundBack } from "react-icons/io";
import { MdOutlineSave } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import { SettingContext } from "../Settings/Reducer/SettingProvider";
import DeployTab from "./tabs/DeployTab";
import KnowledgeTab from "./tabs/KnowledgeTab";
import LogicTab from "./tabs/LogicTab";
import ToolsTab from "./tabs/ToolsTab";
import WidgetPreview from "./WidgetPreview";

const isVoice = (type) => type === "phone" || type === "realtime_openai";

const AssistantBuilder2 = () => {
  const baseUrl = getHostDomain();
  const { assistantId } = useParams();
  const navigate = useNavigate();
  const agency = useContext(AgencyContext);
  const { textColor, buttonColorScheme } = agency;
  const { state } = useContext(SettingContext);

  const [loadStatus, setLoadStatus] = useState("idle");
  const [saveStatus, setSaveStatus] = useState("idle");
  const [voiceSsts, setVoiceSsts] = useState([]);
  const [voices, setVoices] = useState([]);

  const languages = voiceSsts.map((l) => ({
    label: l.name,
    value: l.locale_code,
    id: l.id,
    language_group: l.language_group,
    voicestt_provider: l.voicestt_provider,
  }));

  const getVoices = async (queries = {}) => {
    const { searchQuery, provider, language } = queries;
    const parts = [];
    if (searchQuery) parts.push(`search_term=${searchQuery}`);
    if (provider) parts.push(`provider=${provider}`);
    if (language) parts.push(`language=${language}`);
    const qs = parts.length ? `&${parts.join("&")}` : "";
    const { data } = await fetchController(
      baseUrl + `/api/v1/voice/list/voicetts?size=1000${qs}`
    );
    setVoices(data.items);
    return data;
  };
  const [myownVoiceOptions, setMyownVoiceOptions] = useState([]);
  const [linkAssistantTools, setLinkAssistantTools] = useState([]);
  const [member, setMember] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const methods = useForm({
    defaultValues: {
      assistant_type: "simple",
      name: "",
      description: "",
      systemPrompt: "",
      llmModel: null,
      voice: true,
      voiceLanguages: [],
      custom_voice: false,
      hide_ds: false,
      has_human_agent: false,
      use_tools: false,
      show_images: false,
      attributes: {
        conversation: false,
        contact: false,
        first_sentence: "",
        interruption_time: 10,
      },
      conversation_flow_id: null,
    },
  });

  const { handleSubmit, watch, setValue, reset } = methods;
  const assistantType = watch("assistant_type");
  const voice = isVoice(assistantType);

  const getAssistantTool = async () => {
    if (!assistantId) return;
    try {
      const res = await fetchController(
        baseUrl + `/api/v1/assistant/${assistantId}/LinkAssistantTool`,
        "GET"
      );
      const tools = res?.data?.items || [];
      setLinkAssistantTools(tools);
      setValue("use_tools", tools.length > 0);
    } catch (e) {
      console.error(e);
    }
  };

  // Load voices & assistant data
  useEffect(() => {
    const load = async () => {
      setLoadStatus("loading");
      try {
        // Load voice STTs
        const sttRes = await fetchController(
          baseUrl + "/api/v1/voice/list/voicestt?size=1000"
        );
        setVoiceSsts(sttRes.data.items);

        // Load voice TTSs
        const ttsRes = await fetchController(
          baseUrl + "/api/v1/voice/list/voicetts?size=1000"
        );
        setVoices(ttsRes.data.items);

        // Load custom voices
        const ownVoiceRes = await fetchController(
          baseUrl + "/api/v1/voice/list",
          "GET"
        );
        setMyownVoiceOptions(
          ownVoiceRes?.data?.items?.map((v) => ({ value: v.id, label: v.name })) || []
        );

        setMember(state?.credits?.human_agents > 0);

        // Load existing assistant
        if (assistantId) {
          const { data } = await fetchController(
            baseUrl + `/api/v1/assistant/${assistantId}`
          );

          // Load tools
          const toolsRes = await fetchController(
            baseUrl + `/api/v1/assistant/${assistantId}/LinkAssistantTool`,
            "GET"
          );
          const tools = toolsRes?.data?.items || [];
          setLinkAssistantTools(tools);

          // Set form values
          reset({
            id: data.id,
            assistant_type: data.assistant_type,
            name: data.name,
            description: data.description,
            systemPrompt: data.system_prompt || "",
            llmModel: data.llm_model
              ? { value: data.llm_model, label: data.llm_model }
              : null,
            voice: data.voice ?? true,
            voiceLanguages: [], // populated by VoicesField
            custom_voice: data.custom_voice || false,
            hide_ds: data.hide_ds || false,
            has_human_agent: data.has_human_agent || false,
            use_tools: tools.length > 0,
            show_images: data.show_images || false,
            attributes: data.attributes || {},
            conversation_flow_id: data.conversation_flow_id || null,
          });
        }
      } catch (err) {
        toast.error("Failed to load assistant data");
      } finally {
        setLoadStatus("idle");
      }
    };
    load();
  }, [assistantId]);

  const onSave = async (data, exit = false) => {
    setSaveStatus("saving");
    try {
      const attributes = { ...(data.attributes || {}) };
      if (attributes.detect_voicemail) {
        attributes.detect_voicemail = "hangup";
      }
      if (attributes.conversation_timeout) {
        attributes.conversation_timeout = attributes.conversation_timeout * 60;
      }

      const body = {
        assistant_type: data.assistant_type || "simple",
        llm_model: data.llmModel?.value || "gpt-3.5-turbo-1106",
        name: data.name || "",
        description: data.description || "",
        use_tools: data.use_tools,
        attributes,
        system_prompt: data.systemPrompt,
        voice: data.voice,
        voice_languages:
          data.voiceLanguages?.map((lang) => ({
            stt: lang.id,
            tts: lang.speaker?.value,
          })) || [],
        custom_voice: data.custom_voice,
        show_images: data.show_images,
        hide_ds: data.hide_ds || false,
        has_human_agent: data.has_human_agent,
        conversation_flow_id: data.conversation_flow_id || null,
      };

      await fetchController(
        baseUrl + `/api/v1/assistant/${assistantId}`,
        "PUT",
        body
      );
      toast.success("Assistant saved");
      if (exit) navigate("/ai-agents/assistants2");
    } catch {
      toast.error("Failed to save assistant");
    } finally {
      setSaveStatus("idle");
    }
  };

  if (loadStatus === "loading") {
    return (
      <Flex h="100%" w="100%" justify="center" align="center">
        <Spinner size="xl" />
      </Flex>
    );
  }

  const tabs = [
    { label: "Logic" },
    { label: "Knowledge" },
    { label: "Tools" },
    { label: "Deploy" },
  ];

  return (
    <FormProvider {...methods}>
      <Flex h="100%" w="100%" overflow="hidden">
        {/* Left panel */}
        <Box
          w="460px"
          minW="460px"
          h="100%"
          overflowY="auto"
          borderRight="1px solid"
          borderColor="gray.200"
          display="flex"
          flexDirection="column"
        >
          {/* Top bar */}
          <Flex
            px={4}
            py={3}
            borderBottom="1px solid"
            borderColor="gray.200"
            alignItems="center"
            justifyContent="space-between"
            bg="white"
            position="sticky"
            top={0}
            zIndex={10}
          >
            <Flex alignItems="center" gap={2}>
              <Icon
                as={IoMdArrowRoundBack}
                cursor="pointer"
                onClick={() => navigate("/ai-agents/assistants2")}
                boxSize={5}
              />
              <Box>
                <Text fontWeight="bold" fontSize="sm" noOfLines={1}>
                  {watch("name") || "Assistant"}
                </Text>
                <Text fontSize="xs" color={voice ? "purple.500" : "blue.500"}>
                  {voice ? "Voice Agent Builder" : "Chat Assistant Builder"}
                </Text>
              </Box>
            </Flex>
            <Button
              size="sm"
              colorScheme={buttonColorScheme}
              leftIcon={<MdOutlineSave />}
              isLoading={saveStatus === "saving"}
              onClick={handleSubmit((d) => onSave(d, false))}
            >
              Save
            </Button>
          </Flex>

          {/* Tabs */}
          <Tabs
            index={tabIndex}
            onChange={setTabIndex}
            variant="line"
            flex={1}
            display="flex"
            flexDirection="column"
          >
            <TabList px={4} bg="white" position="sticky" top="57px" zIndex={9}>
              {tabs.map((t) => (
                <Tab key={t.label} fontSize="sm" fontWeight="medium">
                  {t.label}
                </Tab>
              ))}
            </TabList>

            <TabPanels flex={1} overflowY="auto">
              <TabPanel p={4}>
                <LogicTab
                  languages={languages}
                  voices={voices}
                  getVoices={getVoices}
                  myownVoiceOptions={myownVoiceOptions}
                  member={member}
                />
              </TabPanel>
              <TabPanel p={4}>
                <KnowledgeTab assistantId={assistantId} />
              </TabPanel>
              <TabPanel p={4}>
                <ToolsTab
                  assistantId={assistantId}
                  linkAssistantTools={linkAssistantTools}
                  setLinkAssistantTools={setLinkAssistantTools}
                  getAssistantTool={getAssistantTool}
                />
              </TabPanel>
              <TabPanel p={4}>
                <DeployTab assistantId={assistantId} assistantType={assistantType} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

        {/* Right panel — preview */}
        <Box flex={1} h="100%" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
          <WidgetPreview isVoice={voice} assistantName={watch("name")} />
        </Box>
      </Flex>
    </FormProvider>
  );
};

export default AssistantBuilder2;
